import { getHotelCoordinates } from './mapCoordinates';

export const normalizeBounds = (raw) => {
  if (!raw) return null;
  const north = Number(raw.north);
  const south = Number(raw.south);
  const east = Number(raw.east);
  const west = Number(raw.west);
  if (![north, south, east, west].every(Number.isFinite)) return null;
  if (north <= south) return null;
  return { north, south, east, west };
};

export const boundsFromCenter = (lat, lng, radiusKm = 18) => {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((latNum * Math.PI) / 180));
  return {
    north: latNum + latDelta,
    south: latNum - latDelta,
    east: lngNum + lngDelta,
    west: lngNum - lngDelta,
  };
};

export const boundsFromNominatim = (bbox) => {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  return normalizeBounds({
    south: parseFloat(bbox[0]),
    north: parseFloat(bbox[1]),
    west: parseFloat(bbox[2]),
    east: parseFloat(bbox[3]),
  });
};

export const boundsCenter = (bounds) => {
  if (!bounds) return null;
  return {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  };
};

export const zoomForBounds = (bounds) => {
  if (!bounds) return 12;
  const latSpan = Math.abs(bounds.north - bounds.south);
  if (latSpan > 10) return 6;
  if (latSpan > 5) return 7;
  if (latSpan > 2) return 9;
  if (latSpan > 1) return 10;
  if (latSpan > 0.4) return 11;
  if (latSpan > 0.15) return 12;
  return 13;
};

export const isInBounds = (lat, lng, bounds) => {
  if (!bounds || lat == null || lng == null) return false;
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return false;
  return (
    latNum >= bounds.south &&
    latNum <= bounds.north &&
    lngNum >= bounds.west &&
    lngNum <= bounds.east
  );
};

export const boundsChangedEnough = (committed, current) => {
  const a = normalizeBounds(committed);
  const b = normalizeBounds(current);
  if (!a || !b) return false;

  const centerA = boundsCenter(a);
  const centerB = boundsCenter(b);
  const span = Math.max(a.north - a.south, a.east - a.west, 0.01);

  const moved =
    Math.abs(centerA.lat - centerB.lat) > span * 0.12 ||
    Math.abs(centerA.lng - centerB.lng) > span * 0.12;

  const sizeChanged =
    Math.abs(a.north - a.south - (b.north - b.south)) > span * 0.2 ||
    Math.abs(a.east - a.west - (b.east - b.west)) > span * 0.2;

  return moved || sizeChanged;
};

const normalizeLocationToken = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/\b(?:india|karnataka|kerala|goa|maharashtra|tamil nadu)\b/gi, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const CITY_ALIASES = {
  mangalore: ['mangalore', 'mangaluru'],
  mangaluru: ['mangalore', 'mangaluru'],
  bengaluru: ['bengaluru', 'bangalore', 'bangaluru'],
  bangalore: ['bengaluru', 'bangalore', 'bangaluru'],
  mysore: ['mysore', 'mysuru'],
  mysuru: ['mysore', 'mysuru'],
  udupi: ['udupi', 'uduppi'],
  calcutta: ['kolkata', 'calcutta'],
  kolkata: ['kolkata', 'calcutta'],
};

const locationTokensMatch = (a, b) => {
  if (!a || !b) return false;
  if (a === b || a.includes(b) || b.includes(a)) return true;

  const aliasesA = CITY_ALIASES[a] || [a];
  const aliasesB = CITY_ALIASES[b] || [b];
  return aliasesA.some((token) => aliasesB.includes(token) || b.includes(token) || token.includes(b));
};

export const hotelMatchesLocationText = (hotel, searchLabel) => {
  const label = normalizeLocationToken(searchLabel);
  if (!label) return true;

  const city = normalizeLocationToken(hotel.city);
  const address = normalizeLocationToken(hotel.address);
  const name = normalizeLocationToken(hotel.name);

  if (locationTokensMatch(city, label)) return true;
  if (address && (address.includes(label) || label.split(' ').some((part) => part.length > 2 && address.includes(part)))) {
    return true;
  }
  if (name && name.includes(label)) return true;

  const labelParts = label.split(' ').filter((p) => p.length > 2);
  if (labelParts.length) {
    if (city && labelParts.some((part) => city.includes(part) || locationTokensMatch(city, part))) return true;
    if (address && labelParts.some((part) => address.includes(part))) return true;
  }
  return false;
};

export const filterHotelsInArea = (
  hotels,
  bounds,
  searchLabel,
  pinCoordsById = {},
  mode = 'city'
) => {
  const area = normalizeBounds(bounds);
  if (!area) return hotels;

  return hotels.filter((hotel) => {
    if (mode === 'city' && searchLabel && hotelMatchesLocationText(hotel, searchLabel)) {
      return true;
    }

    const id = String(hotel._id || hotel.id);
    const pin = pinCoordsById[id];
    if (pin) return isInBounds(pin.lat, pin.lng, area);

    const coords = getHotelCoordinates(hotel);
    if (coords) return isInBounds(coords.lat, coords.lng, area);

    return mode === 'city' && searchLabel ? hotelMatchesLocationText(hotel, searchLabel) : false;
  });
};

export const leafletBoundsToArea = (leafletBounds) =>
  normalizeBounds({
    north: leafletBounds.getNorth(),
    south: leafletBounds.getSouth(),
    east: leafletBounds.getEast(),
    west: leafletBounds.getWest(),
  });

export const googleBoundsToArea = (googleBounds) =>
  normalizeBounds({
    north: googleBounds.getNorthEast().lat(),
    south: googleBounds.getSouthWest().lat(),
    east: googleBounds.getNorthEast().lng(),
    west: googleBounds.getSouthWest().lng(),
  });
